import { NODE_SPECS, nodeRole, isKnownNodeType, type FlowGraph } from '@/features/flows/types.ts'

/**
 * The paths a template may name, offered as a list beside the raw `{{ }}` box.
 *
 * **Events are a closed union, so the list is derivable rather than typed by
 * hand** (D5). Three sources feed it and they are different in kind, which is
 * why they are built separately rather than concatenated from one table:
 *
 * - `$lead.*` — the source card, present for every trigger.
 * - `$event.*` — the trigger's own payload, and therefore *per trigger type*:
 *   a `lead.status_changed` carries `toStatusId`/`fromStatusId` and a
 *   `lead.received` does not, so offering both would suggest a value that will
 *   render empty at run time.
 * - `<key>.*` — what an *earlier* node produced. Keyed by the node's `key`
 *   rather than its id, because that is what a template writes and what a run
 *   step falls back to when the node is gone.
 *
 * The list is a convenience, never a constraint: the raw input stays, because
 * `validateFlowGraph` is the thing that decides whether a template is legal and
 * the drawer must not become a second, stricter opinion.
 */

/** The source card's own fields, available under every trigger. */
const LEAD_PATHS: readonly string[] = [
  '$lead.id',
  '$lead.name',
  '$lead.price',
  '$lead.funnel',
  '$lead.statusId',
  '$lead.pipelineId',
  '$lead.assignedManagerId',
]

/**
 * The trigger payload, per trigger type.
 *
 * `trigger.status_entered` projects **two** event types (ADR-0003 §7) — a card
 * first seen in the status emits `lead.received`, a card moved into it emits
 * `lead.status_changed` — so `toStatusId` is offered and `fromStatusId` is not:
 * only one of the two events carries a previous status, and a path that is
 * empty half the time reads as a bug rather than as a choice.
 */
const EVENT_PATHS: Record<string, readonly string[]> = {
  'trigger.status_entered': ['$event.leadId', '$event.statusId', '$event.toStatusId'],
}

export interface ChainPath {
  /** The full path, exactly as it goes inside `{{ }}`. */
  path: string
  /** Which of the three groups it came from, for the picker's headings. */
  group: 'lead' | 'event' | 'node'
}

/**
 * Every path a template in this graph may name.
 *
 * It does not depend on which node is being edited, and that is a consequence
 * of the v1 shape rather than a simplification — see the note in the body about
 * why no node output is offered.
 */
export function chainPathsFor(graph: FlowGraph): ChainPath[] {
  const paths: ChainPath[] = LEAD_PATHS.map((path) => ({ path, group: 'lead' as const }))

  const trigger = graph.nodes.find((n) => isKnownNodeType(n.type) && nodeRole(n.type) === 'trigger')
  if (trigger !== undefined) {
    for (const path of EVENT_PATHS[trigger.type] ?? []) paths.push({ path, group: 'event' })
  }

  // **No node outputs are offered, and that is a v1 fact rather than an
  // omission.** The only node with an output is an action, and `checkPath`
  // accepts `<key>.…` only from a node *upstream* of the reader — while
  // `action_has_successor` forbids anything downstream of the one action a
  // graph may hold. So no node can legally read an action's output, and
  // offering `made.leadId` would only ever hand back a path that refuses the
  // save with `template_not_upstream`. The `node` group returns the day a graph
  // may hold a second action or a step after one.

  return paths
}

/** `{{ path }}` — the spelling the engine's template reader expects. */
export const asTemplate = (path: string): string => `{{ ${path} }}`

/** Whether this field may carry a template at all, per its own spec (ADR-0007 §6). */
export function isTemplatable(type: string, fieldName: string): boolean {
  if (!isKnownNodeType(type)) return false
  return NODE_SPECS[type].fields.find((f) => f.name === fieldName)?.templatable === true
}
